import { WHATSAPP_URL } from "../App";
import { Instagram, Facebook } from "lucide-react";

const t = {
  es: {
    tagline: "Creando momentos inolvidables junto a la costa del Pacífico. Bungalows de lujo, piscinas y acceso privado a la playa.",
    quick: "Accesos Rápidos",
    follow: "Síguenos",
    links: [
      { label: "Inicio", href: "#hero" },
      { label: "Nosotros", href: "#about" },
      { label: "Servicios", href: "#services" },
      { label: "Galería", href: "#gallery" },
      { label: "Precios", href: "#pricing" },
      { label: "FAQ", href: "#faq" },
      { label: "Contacto", href: "#contact" },
    ],
    copyright: "© 2024 Berlisa del Pacífico. Todos los derechos reservados.",
  },
  en: {
    tagline: "Creating unforgettable moments by the Pacific coast. Luxury bungalows, swimming pools, and private beach access.",
    quick: "Quick Links",
    follow: "Follow Us",
    links: [
      { label: "Home", href: "#hero" },
      { label: "About", href: "#about" },
      { label: "Services", href: "#services" },
      { label: "Gallery", href: "#gallery" },
      { label: "Prices", href: "#pricing" },
      { label: "FAQ", href: "#faq" },
      { label: "Contact", href: "#contact" },
    ],
    copyright: "© 2024 Berlisa del Pacífico. All rights reserved.",
  },
};

export default function Footer({ lang }) {
  const tr = t[lang];

  return (
    <footer
      data-testid="footer"
      className="bg-[#1F3A5E] text-white pt-16 pb-8"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-12 mb-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-[#D4B895]/40">
                <img
                  src="/images/logo.jpg"
                  alt="Berlisa del Pacífico"
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="font-montserrat font-bold text-white text-lg">
                Berlisa del Pacífico
              </span>
            </div>
            <p className="font-dm-sans text-white/60 text-sm leading-relaxed max-w-xs">
              {tr.tagline}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <p className="font-montserrat font-bold text-[#D4B895] text-xs tracking-widest uppercase mb-5">
              {tr.quick}
            </p>
            <ul className="space-y-2.5">
              {tr.links.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="font-dm-sans text-white/60 text-sm hover:text-white transition-colors duration-200"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Social + Contact */}
          <div>
            <p className="font-montserrat font-bold text-[#D4B895] text-xs tracking-widest uppercase mb-5">
              {tr.follow}
            </p>
            <div className="flex items-center gap-3 mb-6">
              <a
                href="https://instagram.com/berlisadelpacifico"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors duration-200"
              >
                <Instagram size={17} />
              </a>
              <a
                href="#"
                data-testid="footer-facebook"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors duration-200"
              >
                <Facebook size={17} />
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp"
                className="w-10 h-10 rounded-full bg-[#25D366]/20 flex items-center justify-center hover:bg-[#25D366]/40 transition-colors duration-200"
              >
                <svg className="w-4 h-4" fill="white" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </a>
            </div>
            <div className="space-y-1">
              <a
                href="mailto:berlisadepacifico@gmail.com"
                className="font-dm-sans text-white/60 text-sm block hover:text-white transition-colors duration-200"
              >
                berlisadepacifico@gmail.com
              </a>
              <a
                href="tel:+50251995814"
                className="font-dm-sans text-white/60 text-sm block hover:text-white transition-colors duration-200"
              >
                +502 5199 5814
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 pt-6 text-center">
          <p className="font-dm-sans text-white/40 text-xs">{tr.copyright}</p>
        </div>
      </div>
    </footer>
  );
}
